import { _basePortalApi } from "./base";
import { ATK_LIKE_CLASSES, RobotClass } from "common/utils/robots";
import { RobotSummaryResponse } from "protos/portal/robots";

enum RobotApiTag {
  ROBOT = "robot",
  ALARM = "alarm",
  CONFIG = "config",
}

const portalWithRobotTags = _basePortalApi.enhanceEndpoints({
  addTagTypes: Object.values(RobotApiTag),
});

// shared between listRobots and listAtkRobots
type ListRobotsMetricsArgs =
  | { excludeMetrics: true }
  | {
      excludeMetrics?: false;
      latestMetrics?: boolean;
      useJigsaw: boolean;
    };

type ListRobotsArgs = {
  excludeCustomer?: boolean;
  showInternal?: boolean;
  showOffline?: boolean;
  withAlarms?: boolean;
} & ListRobotsMetricsArgs;

const buildListRobotsParams = (
  args: ListRobotsArgs
): Record<string, unknown> => {
  const {
    excludeCustomer,
    showInternal = true,
    showOffline = true,
    withAlarms,
  } = args;
  const baseParams = {
    excludeCustomer,
    showInternal,
    showOffline,
    withAlarms,
  };
  return args.excludeMetrics
    ? { ...baseParams, excludeMetrics: true }
    : {
        ...baseParams,
        excludeMetrics: false,
        latestMetrics: args.latestMetrics ?? true,
        useJigsaw: args.useJigsaw,
      };
};

const transformListRobotsResponse = (
  response: RobotSummaryResponse[] = []
): RobotSummaryResponse[] =>
  response.map((summary) => RobotSummaryResponse.fromJSON(summary));

type RobotTag = RobotApiTag | { type: RobotApiTag; id?: number };

const provideListRobotsTags = (
  summaries?: RobotSummaryResponse[]
): RobotTag[] => [
  RobotApiTag.ROBOT,
  ...(summaries?.map((summary) => ({
    type: RobotApiTag.ROBOT,
    id: summary.robot?.db?.id,
  })) ?? []),
];

export const robotApi = portalWithRobotTags.injectEndpoints({
  endpoints: (builder) => ({
    // Lean list-robots query (portal has its own richer listRobots; results
    // here are unsorted). For ATK-only robots, prefer listAtkRobots below.
    listRobots: builder.query<
      RobotSummaryResponse[],
      { classes?: RobotClass[] } & ListRobotsArgs
    >({
      keepUnusedDataFor: 60 * 1, // 1 minute
      query: (args) => ({
        url: `robots`,
        params: {
          classes: args.classes?.join(","),
          ...buildListRobotsParams(args),
        },
      }),
      transformResponse: transformListRobotsResponse,
      providesTags: provideListRobotsTags,
    }),
    // listRobots restricted to ATK-like classes (RTCs, simulators, and
    // mystery robots) - see ATK_LIKE_CLASSES.
    listAtkRobots: builder.query<RobotSummaryResponse[], ListRobotsArgs>({
      keepUnusedDataFor: 60 * 1, // 1 minute
      query: (args) => ({
        url: `robots`,
        params: {
          classes: ATK_LIKE_CLASSES.join(","),
          ...buildListRobotsParams(args),
        },
      }),
      transformResponse: transformListRobotsResponse,
      providesTags: provideListRobotsTags,
    }),
    getRobot: builder.query<RobotSummaryResponse, { serial: string }>({
      query: ({ serial }) => `robots/${serial}`,
      providesTags: (summary) => [
        { type: RobotApiTag.ROBOT, id: summary?.robot?.db?.id },
        { type: RobotApiTag.CONFIG, id: summary?.robot?.serial },
        ...(summary?.alarmList
          ? summary.alarmList.map((alarm) => ({
              type: RobotApiTag.ALARM,
              id: alarm.db?.id,
            }))
          : []),
      ],
      transformResponse: (summary: RobotSummaryResponse) =>
        RobotSummaryResponse.fromJSON(summary),
    }),
  }),
});
